<template>
    <AuthLayout>
        <div
            class="flex w-full flex-col items-stretch rounded-[8px] bg-white px-[22px] pt-[42px] pb-[42px] sm:max-w-[374px] rounded-[12.8px] border border-[#E5E7EB]"
        >
            <div class="flex justify-center">
                <div
                    class="lock-anim h-[160px] w-[160px]"
                    aria-hidden="true"
                    v-html="lockSvgRaw"
                />
            </div>

            <h1
                class="mt-6 text-center text-3xl font-bold leading-9 text-[#161E2E]"
            >
                {{ $t("vue.reset_your_password") }}
            </h1>

            <div
                class="mx-auto mt-6 max-w-[344px] text-center text-[12.25px] font-normal leading-[1.42] text-black"
            >
                {{ $t("vue.enter_your_email_and_we_will_send_instructions") }}
            </div>

            <form class="mt-6 w-full" @submit.prevent="submit">
                <div class="w-full">
                    <TextInput
                        id="email"
                        v-model="state.form.email"
                        type="email"
                        required
                        suppress-required-icon
                        autofocus
                        :label="$t('vue.email_address')"
                        :placeholder="$t('vue.enter_your_email_address')"
                        name="email"
                        autocomplete="email"
                        :error="getError('email')"
                        @blur="touchIfNotEmpty()"
                    />
                </div>

                <div class="mt-6 flex flex-col items-center">
                    <MegaButton
                        id="login-button"
                        class="w-full !rounded-md !bg-[#088BEA] !py-3 !text-sm !font-medium !text-white !shadow-none hover:!bg-[#067DD2] focus:!ring-0"
                        :disabled="loading"
                        type="submit"
                    >
                        {{ $t("vue.send_password_reset_link") }}
                    </MegaButton>

                    <p
                        class="mt-[24px] text-center text-[12.25px] text-gray-500"
                    >
                        {{ $t("vue.go_back_to") }}
                        <inertia-link
                            :href="route('login')"
                            class="mt-6 pb-2 text-xs font-medium text-[#088BEA] hover:text-[#067DD2]"
                        >
                            {{ $t("vue.sign_in_exclamation") }}
                        </inertia-link>
                    </p>
                </div>
            </form>
        </div>
    </AuthLayout>
</template>

<script lang="ts" setup>
import { reactive, ref } from "vue";
import route from "ziggy-js";
import { required, email, maxLength } from "@vuelidate/validators";
import { useReCaptcha } from "vue-recaptcha-v3";
import useVuelidate from "@vuelidate/core";
import { router } from "@inertiajs/vue3";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import { useTranslations } from "@/composables/useTranslations";
import lockSvgRaw from "@/assets/images/svg-lock.svg?raw";

const reCaptcha = useReCaptcha();

// Get access to translations
const { t } = useTranslations();

const state = reactive({
    form: {
        email: "",
        recaptcha_token: "",
    },
});

const rules = {
    form: {
        email: { required, email, maxLength: maxLength(255) },
    },
};

const loading = ref(false);
const errors = ref<IInputErrors>({});
const v$ = useVuelidate(rules, state);

const touchIfNotEmpty = () => {
    const rawValue = state.form.email;
    const value = rawValue.trim();

    if (value !== "") {
        v$.value.form.email.$touch();
    }
};

// const svgLock = new URL(
//     "/resources/js/assets/images/svg-lock.svg",
//     import.meta.url,
// ).href;

const submit = async () => {
    v$.value.form.$touch();
    if (v$.value.form.$error) {
        () => undefined;
    } else {
        loading.value = true;
        let token = undefined;

        try {
            await reCaptcha?.recaptchaLoaded();
            token = await reCaptcha?.executeRecaptcha("login");

            if (token === undefined) {
                throw new Error();
            }
        } catch (e) {
            loading.value = false;
            errors.value = {
                email: t("vue.recaptcha_validation_failed"),
            };
            return;
        }

        state.form.recaptcha_token = token;
        router.post(route("password.email"), state.form, {
            preserveScroll: true,
            onError: (err) => {
                errors.value = err;
            },
            onFinish: () => {
                loading.value = false;
            },
        });
    }
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>

<style scoped>
.lock-anim {
    overflow: hidden;
    line-height: 0;
    isolation: isolate;
}

.lock-anim :deep(svg) {
    width: 160px;
    height: 160px;
    display: block;
}

.lock-anim :deep(#backLock) {
    transform-box: fill-box;
    animation: floatBack 8.8s ease-in-out infinite;
    will-change: transform;
}

.lock-anim :deep(#frontLock) {
    transform-box: fill-box;
    animation: floatFront 3.8s ease-in-out infinite;
    will-change: transform;
}

.lock-anim :deep(#backLock),
.lock-anim :deep(#frontLock) {
    transform-origin: 50% 60%;
}

@keyframes floatFront {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-7px) rotate(-1.4deg);
    }
}

@keyframes floatBack {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-3px) rotate(-0.6deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .lock-anim :deep(#backLock),
    .lock-anim :deep(#frontLock) {
        animation: none !important;
    }
}
</style>
